/**
 * Adaptive learning — tunable constants and weights.
 * All formulas use these so behavior can be adjusted in one place.
 */

import type { DifficultyLevel } from "./types";

/** Difficulty score range: 0 = easiest, 100 = hardest */
export const DIFFICULTY = {
  SCORE_MIN: 0,
  SCORE_MAX: 100,
  /** Thresholds for mapping score -> level (inclusive upper bound for each band) */
  LEVEL_BANDS: {
    easy: 25,
    medium: 50,
    hard: 75,
    critical: 100,
  } as Record<DifficultyLevel, number>,
  /** Weight: error rate (incorrect / seen) */
  WEIGHT_ERROR_RATE: 0.35,
  /** Weight: recent failures (last N answers) */
  WEIGHT_RECENT_FAILURES: 0.25,
  /** Weight: response latency (slower = slightly harder) */
  WEIGHT_LATENCY: 0.1,
  /** Weight: hint/explain usage */
  WEIGHT_HINT_EXPLAIN: 0.15,
  /** Weight: streak instability (broken streaks, low streak) */
  WEIGHT_STREAK: 0.15,
  /** Half-life in events for "recent" weighting (exponential decay) */
  RECENT_HALFLIFE_EVENTS: 5,
} as const;

/** Memory strength: 0 = none, 100 = very strong */
export const MEMORY = {
  STRENGTH_MIN: 0,
  STRENGTH_MAX: 100,
  /** Base decay per day when not reviewed (reduces strength) */
  DECAY_PER_DAY: 4,
  /** Gain per correct review (capped) */
  GAIN_PER_CORRECT: 8,
  /** Penalty per incorrect (strong) */
  PENALTY_PER_INCORRECT: 20,
  /** Forgetting risk: 0 = none, 100 = very high */
  RISK_MAX: 100,
  /** Days after which we consider "overdue" to start increasing risk quickly */
  OVERDUE_DAYS_THRESHOLD: 1,
  /** Base interval (days) for next review when strength is low */
  BASE_INTERVAL_DAYS: 1,
  /** Max interval (days) for next review */
  MAX_INTERVAL_DAYS: 60,
} as const;

/** Review priority: higher = show first in Smart Review */
export const REVIEW_PRIORITY = {
  /** Weight: forgetting risk (0–100) */
  WEIGHT_FORGETTING_RISK: 0.4,
  /** Weight: difficulty score (0–100) */
  WEIGHT_DIFFICULTY: 0.25,
  /** Weight: overdue score (how many days overdue) */
  WEIGHT_OVERDUE: 0.25,
  /** Weight: recency (inverse: not seen for a while = higher) */
  WEIGHT_RECENCY: 0.1,
  /** Max consecutive critical cards before inserting a buffer card */
  MAX_CONSECUTIVE_CRITICAL: 2,
  /** After this many hard/critical, inject one easier card */
  INJECT_EASIER_AFTER: 3,
} as const;

/** Recommended mode thresholds */
export const RECOMMENDED_MODE = {
  /** Memory strength below this -> suggest "learn" */
  LEARN_BELOW_STRENGTH: 30,
  /** Memory strength above this and stable -> suggest "test" */
  TEST_ABOVE_STRENGTH: 60,
  /** Difficulty "critical" or "hard" -> prefer "learn" or "cards" */
  HARD_PREFER_LEARN: true,
} as const;

/** Defaults for new/empty progress rows */
export const DEFAULTS = {
  DIFFICULTY_LEVEL: "medium" as DifficultyLevel,
  DIFFICULTY_SCORE: 50,
  MEMORY_STRENGTH: 0,
  FORGETTING_RISK: 80,
  NEXT_REVIEW_DAYS: 1,
  RECOMMENDED_MODE: "cards" as const,
} as const;
